import { useState, useEffect, useRef } from 'react';

interface StatItem {
  value: number;
  prefix?: string;
  suffix: string;
  label: string;
  sublabel: string;
  icon: string;
  color: string;
  bg: string;
}

const stats: StatItem[] = [
  {
    value: 1.2,
    prefix: '$',
    suffix: 'B+',
    label: 'Assets Verified',
    sublabel: 'Total institutional value processed through BPI compliance framework',
    icon: 'ri-bar-chart-box-line',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10 border-emerald-500/20',
  },
  {
    value: 4500,
    prefix: '',
    suffix: '+',
    label: 'Malicious Contracts Flagged',
    sublabel: 'Honey pots, frozen contracts, and exploitable ERC-20 vulnerabilities neutralized',
    icon: 'ri-bug-2-line',
    color: 'text-red-400',
    bg: 'bg-red-500/10 border-red-500/20',
  },
  {
    value: 120,
    prefix: '',
    suffix: '+',
    label: 'Institutional Partners',
    sublabel: 'Hedge funds, family offices, crypto exchanges, and compliance departments',
    icon: 'ri-building-2-line',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10 border-amber-500/20',
  },
  {
    value: 99.7,
    prefix: '',
    suffix: '%',
    label: 'Detection Accuracy',
    sublabel: 'Verified detection rate for blacklisted and non-compliant token addresses',
    icon: 'ri-focus-3-line',
    color: 'text-sky-400',
    bg: 'bg-sky-500/10 border-sky-500/20',
  },
];

function AnimatedCounter({ target, prefix = '', suffix, decimals = 0 }: {
  target: number;
  prefix?: string;
  suffix: string;
  decimals?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const duration = 1800;
          const steps = 60;
          const increment = target / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(current);
            }
          }, duration / steps);
        }
      },
      { threshold: 0.3 }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [target]);

  const display = decimals > 0 ? count.toFixed(decimals) : Math.floor(count).toLocaleString();

  return (
    <div ref={ref} className="text-4xl sm:text-5xl md:text-6xl font-black leading-none" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      {prefix}{display}{suffix}
    </div>
  );
}

export default function LiveStats() {
  return (
    <section id="live-stats" className="bg-white py-16 sm:py-24 px-5 sm:px-10 lg:px-16 border-y border-slate-100">
      <div className="max-w-7xl mx-auto">
        <div className="mb-10 sm:mb-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-emerald-500 rounded-full"></div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
              <span
                className="text-slate-500 text-xs font-bold tracking-[0.2em] uppercase"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Live Platform Statistics
              </span>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 leading-[1.08] tracking-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Trusted by
              <br />
              <span className="text-emerald-600">Global Institutions.</span>
            </h2>
            <p className="text-slate-500 text-base leading-relaxed max-w-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
              Real-time metrics from BPI Holding SA&apos;s institutional compliance operations.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-8 sm:gap-6 mb-10 sm:mb-14">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl p-6 sm:p-8 hover:border-slate-300 transition-all duration-200"
            >
              <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center mb-4 sm:mb-5">
                <i className={`${stat.icon} text-xl ${stat.color}`}></i>
              </div>
              <div className={`mb-1 ${stat.color}`}>
                <AnimatedCounter
                  target={stat.value}
                  prefix={stat.prefix ?? ''}
                  suffix={stat.suffix}
                  decimals={stat.value % 1 !== 0 ? 1 : 0}
                />
              </div>
              <div
                className="text-slate-900 font-bold text-sm sm:text-base mb-2"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {stat.label}
              </div>
              <p className="text-slate-500 text-xs leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                {stat.sublabel}
              </p>
            </div>
          ))}
        </div>

        {/* Certificate strip */}
        <div className="bg-[#001529] rounded-xl sm:rounded-2xl p-5 sm:p-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
            <div className="flex items-center gap-4 sm:gap-5">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-emerald-500/15 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-verified-badge-line text-2xl sm:text-3xl text-emerald-400"></i>
              </div>
              <div>
                <div
                  className="text-white font-bold text-sm sm:text-lg"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  BPI Verification Certificate — Institutional Standard
                </div>
                <div className="text-slate-400 text-xs sm:text-sm mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Issued under Swiss law &nbsp;·&nbsp; FATF &amp; FINMA aligned &nbsp;·&nbsp; 90-day validity &nbsp;·&nbsp; Internationally recognized
                </div>
              </div>
            </div>
            <a
              href="https://mycryptoaml.com/verification"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-5 sm:px-7 py-3 rounded-lg font-bold text-sm sm:text-base transition-all duration-200 whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              <i className="ri-shield-check-line text-lg"></i>
              Get Certified
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
